import { Form as FormikForm, useFormikContext } from 'formik';
import { formikHelpers, lucideIcons } from '@round/creatorbase-ui';
import { creatorbase } from '@round/api';
import styles from './Form.module.css';
import { useInfluencerLocations } from 'Hooks/useInfluencerLocations';
import { makeLocationOption } from 'Hooks/selectOptions/useInfluencerLocationSelect';
import { useMemo } from 'react';
import { useCreatorbaseCurrencies } from 'contexts/CreatorbaseCurrencyContext';
import { mapCurrencyToOption } from 'contexts/OptionsContext/useCurrencies';
import useClientSelect from 'Modules/Plans/Clients/hooks/useClientSelect';
import { useClientContentTags } from '@round/creatorbase-helpers/api-hooks';
import { mapContentTagsToOptions } from 'helpers';
import { FormState, FormValues, getFieldName as getFormikFieldName } from '../helpers';

const { Field, InputField, ComboboxField, MoneyInputField, ComboboxExternalField, TextareaField } = formikHelpers;
const { Link, MapPinIcon, MailIcon, DollarSignIcon, TagIcon, StickyNoteIcon } = lucideIcons;

type Props = {
    platform: creatorbase.Platform;
};

const Form = ({ platform }: Props) => {
    const getFieldName = (name: keyof FormValues) => getFormikFieldName(platform, name);
    const { locations } = useInfluencerLocations();
    const countryOptions = useMemo(() => locations.map(makeLocationOption), [locations]);
    const { currencies } = useCreatorbaseCurrencies();
    const currencyOptions = currencies.map(mapCurrencyToOption);
    const { selectedClientId } = useClientSelect();
    const { tags: contentTags } = useClientContentTags(selectedClientId);
    const contentTagOptions = useMemo(() => mapContentTagsToOptions(contentTags), [contentTags]);
    const formik = useFormikContext<FormState>();
    // common fields are hidden for other active platforms
    const shouldDisplayCommonFields = platform === formik.values.activePlatforms[0];

    return (
        <FormikForm className={styles.form}>
            <Field name={getFieldName('profileUrl')}>
                <Field.Label htmlFor={getFieldName('profileUrl')}>
                    <Link size="14" className={styles.labelIcon} />
                    Profile URL
                </Field.Label>
                <InputField name={getFieldName('profileUrl')} placeholder={`https://www.${platform}.com/@username`} />
                <Field.Error name={getFieldName('profileUrl')} />
            </Field>

            {shouldDisplayCommonFields && (
                <>
                    <Field name="location">
                        <Field.Label htmlFor="location">
                            <MapPinIcon size="14" className={styles.labelIcon} />
                            Location
                        </Field.Label>

                        <ComboboxField name="location" items={countryOptions} placeholder="Select country" />
                        <Field.Error name="location" />
                    </Field>

                    <Field name="contact_details">
                        <Field.Label htmlFor="contact_details">
                            <MailIcon size="14" className={styles.labelIcon} />
                            Contact Information
                        </Field.Label>

                        <InputField name="contact_details" placeholder="Enter contact details" />
                        <Field.Error name="contact_details" />
                    </Field>

                    <Field name="paypal_email">
                        <Field.Label htmlFor="paypal_email">
                            <MailIcon size="14" className={styles.labelIcon} />
                            PayPal Email
                        </Field.Label>

                        <InputField name="paypal_email" placeholder="Enter paypal email" />
                        <Field.Error name="paypal_email" />
                    </Field>
                </>
            )}

            <Field name={getFieldName('cost')}>
                <Field.Label htmlFor={getFieldName('cost')}>
                    <DollarSignIcon size="14" className={styles.labelIcon} />
                    Cost
                </Field.Label>

                <MoneyInputField
                    name={getFieldName('cost')}
                    isClearable
                    placeholder="e.g. 500"
                    currencies={currencyOptions}
                />
                <Field.Error name={getFieldName('cost')} />
            </Field>

            {shouldDisplayCommonFields && (
                <>
                    <Field name="content_tags">
                        <Field.Label htmlFor="content_tags">
                            <TagIcon size="14" className={styles.labelIcon} />
                            Tags
                        </Field.Label>

                        <ComboboxExternalField
                            multiple
                            isClearable
                            name="content_tags"
                            items={contentTagOptions}
                            placeholder="Select tags"
                        />
                        <Field.Error name="content_tags" />
                    </Field>

                    <Field name="notes">
                        <Field.Label htmlFor="notes">
                            <StickyNoteIcon size="14" className={styles.labelIcon} />
                            Notes
                        </Field.Label>
                        <TextareaField name="notes" placeholder="Enter notes" />
                        <Field.Error name="notes" />
                    </Field>
                </>
            )}
        </FormikForm>
    );
};

export default Form;
